import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Urls } from "../../../utils/constant";

import { useApi } from "../apiCall/useApi";

import useFullPageLoader from "../Common/PageLoader/useFullPageLoader";

import {
  hidePageLoader,
  showPageLoader,
} from "../../reducers/Common/PageLoader/PageLoader.reducer";
import {
  getDoctorList,
  getPatientList,
} from "../../selectors/Appointment/appointment.selector";

import {
  setAppointmentList,
  setDoctorComboList,
  setPatientComboList,
} from "../../reducers/Appointment/appointment.reducer";
import { format } from "date-fns";

const COLUMN_LIST = [
  {
    field: "sr_no",
    headerName: "sr. no.",
    flex: 1,
  },
  {
    field: "doctor_val",
    headerName: "Doctor Name",

    flex: 2,
  },
  {
    field: "patient_val",
    headerName: "Patient Name",
    flex: 2,
  },
  {
    field: "appointment_date",
    headerName: "Appointment Date",
    flex: 2,
  },
  {
    field: "appointment_time",
    headerName: "Appointment Time",
    flex: 2,
  },
  {
    field: "name",
    headerName: "Name",
    flex: 1,
  },
  {
    field: "age",
    headerName: "Age",
    flex: 1,
  },
  {
    field: "gender_val",
    headerName: "Gender",
    flex: 1,
  },
  {
    field: "problem",
    headerName: "Problem",
    flex: 2,
  },

  {
    field: "status_val",
    headerName: "Status",
    flex: 1,
  },
];

const StatusList = [
  { id: "All", label: "All" },
  { id: "Pending", label: "Pending" },
  { id: "Completed", label: "Completed" },
  { id: "Not Arrived", label: "Not Arrived" },
  { id: "Cancelled", label: "Cancelled" },
  { id: "Reschedule", label: "Reschedule" },
];

export default function useAppointment() {
  const dispatch = useDispatch();
  const { apiPost } = useApi();
  const [loading, setLoading] = useState(false);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [DoctorId, setDoctorId] = useState("");
  const [PatientId, setPatientId] = useState("");
  const [FromDate, setFromDate] = useState(new Date());
  const [UptoDate, setUptoDate] = useState(new Date());
  const [status, setStatus] = useState("All");
  const doctorList = useSelector(getDoctorList);
  const patientList = useSelector(getPatientList);

  const fetchAppointmentList = useCallback(
    async ({ current_page = 1, row_count }) => {
      if (loading) return;
      setLoading(true);
      try {
        dispatch(showPageLoader());

        const formattedFromDate = format(FromDate, "yyyy-MM-dd");
        const formattedUptoDate = format(UptoDate, "yyyy-MM-dd");

        const { data } = await apiPost({
          url: Urls.appointmentGet(),
          data: {
            patient_id: PatientId,
            from_date: formattedFromDate,
            upto_date: formattedUptoDate,
            status: 0, // <-- always get all from backend; we'll filter by status_val
            page_size: row_count,
            current_page,
          },
        });

        // data?.data is your rows; keep summary as-is if you need
        const rows = data?.data ?? [];

        const filtered =
          status === "All"
            ? rows
            : rows.filter((r) => {
                // normalize just in case
                const sv = (r.status_val || "").trim();
                if (status === "Completed") return sv === "Completed";
                if (status === "Not Arrived") return sv === "Not Arrived";
                if (status === "Pending") return sv === "Pending";
                if (status === "Cancelled") return sv === "Cancelled";
                if (status === "Reschedule") return sv === "Reschedule";
                return true;
              });

        // push filtered list to reducer (preserve any paging meta if you use it)
        dispatch(setAppointmentList({ ...data, data: filtered }));

        setLoading(false);
        dispatch(hidePageLoader());
      } catch (err) {
        dispatch(hidePageLoader());
        console.log(err);
        setLoading(false);
      }
    },
    [loading, dispatch, FromDate, UptoDate, apiPost, PatientId, status]
  );

  const fetchDoctorComboList = useCallback(async () => {
    try {
      const { data } = await apiPost({
        url: Urls.getDoctorCombo(),
        data: {},
      });
      // setting list to reducer

      dispatch(setDoctorComboList(data.data));
    } catch (err) {
      console.log(err);
    }
  }, [DoctorId]);

  const fetchPatientComboList = useCallback(async () => {
    try {
      const { data } = await apiPost({
        url: Urls.getPatientCombo(),
        data: {},
      });
      // setting list to reducer

      dispatch(setPatientComboList(data.data));
    } catch (err) {
      console.log(err);
    }
  }, [PatientId]);

  const finddoctorFromLabel = useCallback(
    (value) => {
      const selectedDoctorId = value
        ? doctorList.find((item) => item.label === value)?.id
        : null;
      setDoctorId(selectedDoctorId);

      return selectedDoctorId;
    },
    [doctorList]
  );

  const findpatientFromLabel = useCallback(
    (value) => {
      const selectedPatientId = value
        ? patientList.find((item) => item.label === value)?.id
        : null;
      setPatientId(selectedPatientId);
      return selectedPatientId;
    },
    [patientList]
  );

  const handleStatusChange = useCallback((e) => {
    setStatus(e.target.value);
  }, []);

  const handleFromDate = useCallback((newDate) => {
    setFromDate(newDate);
  }, []);

  const handleUptoDate = useCallback((newDate) => {
    setUptoDate(newDate);
  }, []);

  return {
    gridHeaders: COLUMN_LIST,
    loading,
    loader,
    status,
    StatusList,
    DoctorId,
    PatientId,
    finddoctorFromLabel,
    findpatientFromLabel,
    handleStatusChange,
    fetchAppointmentList,
    fetchDoctorComboList,
    fetchPatientComboList,
    FromDate,
    UptoDate,
    handleFromDate,
    handleUptoDate,
  };
}
